import type { HttpClient, HttpEvent, HttpRequestOptions, HttpResponse } from "./types.js";
import type { RateLimitOptions } from "../data-model/options.js";
import type { Url } from "../data-model/primitives.js";
import { hostnameOf } from "../utils/url.js";
import { sleep, throwIfAborted } from "../utils/sleep.js";

export const DEFAULT_RATE_LIMIT = {
  requestsPerSecond: 2,
  burst: 2,
} as const satisfies Required<RateLimitOptions>;

const RATE_LIMIT_WAIT_EVENT_THRESHOLD_MS = 50;

interface Bucket {
  tokens: number;
  lastRefill: number;
}

export class RateLimiter {
  readonly #opts: { requestsPerSecond: number; burst: number };
  readonly #buckets = new Map<string, Bucket>();
  readonly #pauses = new Map<string, number>();

  constructor(opts: RateLimitOptions = DEFAULT_RATE_LIMIT) {
    // A request costs one token, so a burst below 1 can never fill enough to
    // issue one. Default it up (a sub-1 rps like 0.5 is a natural polite rate)
    // and reject an explicit sub-1 burst rather than hang forever on acquire().
    const burst = opts.burst ?? Math.max(1, opts.requestsPerSecond);
    if (burst < 1) {
      throw new RangeError(`RateLimiter: burst must be >= 1 (got ${burst})`);
    }
    this.#opts = {
      requestsPerSecond: opts.requestsPerSecond,
      burst,
    };
  }

  async acquire(host: string, signal?: AbortSignal): Promise<void> {
    while (true) {
      throwIfAborted(signal);

      const pauseUntil = this.#pauses.get(host) ?? 0;
      const pauseRemaining = pauseUntil - Date.now();
      if (pauseRemaining > 0) {
        await sleep(pauseRemaining, signal);
        continue;
      }

      const bucket = this.#getBucket(host);
      this.#refill(bucket);
      if (bucket.tokens >= 1) {
        bucket.tokens -= 1;
        return;
      }

      const waitMs = Math.ceil(1000 / this.#opts.requestsPerSecond);
      await sleep(waitMs, signal);
    }
  }

  pauseHost(host: string, ms: number): void {
    this.#pauses.set(host, Date.now() + ms);
  }

  #getBucket(host: string): Bucket {
    let b = this.#buckets.get(host);
    if (!b) {
      b = { tokens: this.#opts.burst, lastRefill: Date.now() };
      this.#buckets.set(host, b);
    }
    return b;
  }

  #refill(bucket: Bucket): void {
    const now = Date.now();
    const elapsedMs = now - bucket.lastRefill;
    if (elapsedMs <= 0) return;
    const refill = (elapsedMs / 1000) * this.#opts.requestsPerSecond;
    bucket.tokens = Math.min(this.#opts.burst, bucket.tokens + refill);
    bucket.lastRefill = now;
  }
}

export class RateLimitedHttpClient implements HttpClient {
  constructor(
    private readonly inner: HttpClient,
    private readonly limiter: RateLimiter,
    private readonly onEvent?: (e: HttpEvent) => void,
  ) {}

  async get(url: Url, opts?: HttpRequestOptions): Promise<HttpResponse> {
    const host = hostnameOf(url);
    const start = Date.now();
    await this.limiter.acquire(host, opts?.signal);
    const waited = Date.now() - start;
    if (waited > RATE_LIMIT_WAIT_EVENT_THRESHOLD_MS) {
      this.onEvent?.({ type: "rate-limit:wait", host, waitMs: waited });
    }
    return this.inner.get(url, opts);
  }
}
